import { useState, useEffect, useCallback } from "react";
import {
  Activity, Server, Database, HardDrive, Cpu, MemoryStick, Wifi, WifiOff,
  RefreshCw, Clock, Shield, Zap, BarChart3, Users, BookOpen, Building2,
  AlertTriangle, CheckCircle2, XCircle, Gauge, Layers, ArrowUpRight,
  ArrowDownRight, TrendingUp, Loader2, ChevronRight
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Separator } from "../../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const STATUS_CONFIG = {
  operational: { label: "Operational", color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", icon: CheckCircle2 },
  degraded: { label: "Degraded", color: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", icon: AlertTriangle },
  major_outage: { label: "Major Outage", color: "bg-red-500", text: "text-red-700", bg: "bg-red-50", border: "border-red-200", icon: XCircle },
  offline: { label: "Offline", color: "bg-red-500", text: "text-red-700", bg: "bg-red-50", border: "border-red-200", icon: XCircle },
};

const StatusDot = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.degraded;
  return (
    <span className="relative flex h-3 w-3" data-testid={`status-dot-${status}`}>
      {status === "operational" && (
        <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", cfg.color)} />
      )}
      <span className={cn("relative inline-flex rounded-full h-3 w-3", cfg.color)} />
    </span>
  );
};

const ServiceCard = ({ service }) => {
  const cfg = STATUS_CONFIG[service.status] || STATUS_CONFIG.degraded;
  const Icon = cfg.icon;
  return (
    <div
      className={cn("flex items-center justify-between p-4 rounded-xl border transition-all", cfg.bg, cfg.border)}
      data-testid={`service-card-${service.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <StatusDot status={service.status} />
        <span className="font-semibold text-[#0f1b2d] text-sm">{service.label}</span>
      </div>
      <div className={cn("flex items-center gap-1.5 text-xs font-medium", cfg.text)}>
        <Icon className="w-3.5 h-3.5" />
        {cfg.label}
      </div>
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, sub, trend, color = "text-[#1e3a5f]" }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className={cn("p-2 rounded-lg bg-slate-100", color)}>
        <Icon className="w-5 h-5" />
      </div>
      {trend && (
        <div className={cn("flex items-center gap-0.5 text-xs font-medium", trend > 0 ? "text-emerald-600" : "text-red-500")}>
          {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="text-2xl font-bold text-[#0f1b2d] tracking-tight">{value}</div>
    <div className="text-xs text-slate-500 mt-1">{label}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

const GaugeRing = ({ percent, label, size = 120, strokeWidth = 10 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  const color = percent > 80 ? "#dc2626" : percent > 60 ? "#d97706" : "#15803d";
  return (
    <div className="flex flex-col items-center gap-2">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color}
          strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="text-center -mt-[calc(50%+16px)]">
        <div className="text-xl font-bold text-[#0f1b2d]">{percent}%</div>
      </div>
      <div className="text-xs text-slate-500 font-medium mt-4">{label}</div>
    </div>
  );
};

const CollectionRow = ({ name, documents, maxDocs }) => (
  <div className="flex items-center gap-3 py-2">
    <div className="w-36 text-sm text-slate-700 font-medium truncate" title={name}>{name}</div>
    <div className="flex-1">
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#1e3a5f] rounded-full transition-all duration-500"
          style={{ width: `${Math.max((documents / maxDocs) * 100, 2)}%` }}
        />
      </div>
    </div>
    <div className="w-16 text-right text-sm font-mono text-slate-600">{documents.toLocaleString()}</div>
  </div>
);

// ═══════════════════════════════════════
// LEARNER VIEW
// ═══════════════════════════════════════
const LearnerHealthView = ({ data, loading, onRefresh }) => (
  <div className="space-y-6" data-testid="learner-health-view">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold text-[#0f1b2d]">Platform Status</h1>
        <p className="text-sm text-slate-500 mt-1">Real-time service availability</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="refresh-btn">
        <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
        Refresh
      </Button>
    </div>

    {/* Overall status banner */}
    <OverallStatusBanner data={data} />

    {/* Services grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {Object.values(data.services || {}).map((s, i) => (
        <ServiceCard key={i} service={s} />
      ))}
    </div>

    {/* Connectivity */}
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {data.connectivity === "connected" ? (
              <Wifi className="w-5 h-5 text-emerald-600" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-500" />
            )}
            <div>
              <div className="font-semibold text-sm text-[#0f1b2d]">Connectivity</div>
              <div className="text-xs text-slate-500">
                Last synced: {data.last_sync ? new Date(data.last_sync).toLocaleTimeString() : "—"}
              </div>
            </div>
          </div>
          <Badge variant={data.connectivity === "connected" ? "default" : "destructive"}>
            {data.connectivity === "connected" ? "Connected" : "Disconnected"}
          </Badge>
        </div>
      </CardContent>
    </Card>

    <SystemInfoFooter data={data} />
  </div>
);

// ═══════════════════════════════════════
// MANAGER VIEW
// ═══════════════════════════════════════
const ManagerHealthView = ({ data, loading, onRefresh }) => (
  <div className="space-y-6" data-testid="manager-health-view">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold text-[#0f1b2d]">System Health</h1>
        <p className="text-sm text-slate-500 mt-1">Platform status & team overview</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="refresh-btn">
        <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
        Refresh
      </Button>
    </div>

    <OverallStatusBanner data={data} />

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {Object.values(data.services || {}).map((s, i) => (
        <ServiceCard key={i} service={s} />
      ))}
    </div>

    {/* Team usage summary */}
    {data.team_summary && (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-[#1e3a5f]" />
            Team Usage Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={Users} label="Team Members" value={data.team_summary.total_members} />
            <MetricCard icon={BookOpen} label="Total Enrollments" value={data.team_summary.total_enrollments} />
            <MetricCard icon={CheckCircle2} label="Completed Courses" value={data.team_summary.completed_courses} color="text-emerald-600" />
            <MetricCard icon={TrendingUp} label="Completion Rate" value={`${data.team_summary.completion_rate}%`} color="text-[#1e3a5f]" />
          </div>
        </CardContent>
      </Card>
    )}

    <SystemInfoFooter data={data} />
  </div>
);

// ═══════════════════════════════════════
// TENANT ADMIN VIEW
// ═══════════════════════════════════════
const TenantAdminHealthView = ({ data, loading, onRefresh }) => (
  <div className="space-y-6" data-testid="tenant-admin-health-view">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold text-[#0f1b2d]">System Health</h1>
        <p className="text-sm text-slate-500 mt-1">Infrastructure status & organization usage</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="refresh-btn">
        <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
        Refresh
      </Button>
    </div>

    <OverallStatusBanner data={data} />

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {Object.values(data.services || {}).map((s, i) => (
        <ServiceCard key={i} service={s} />
      ))}
    </div>

    {/* Tenant usage stats */}
    {data.tenant_usage && (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#1e3a5f]" />
            Organization Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={Users} label="Total Users" value={data.tenant_usage.total_users} />
            <MetricCard icon={Zap} label="Active (7 days)" value={data.tenant_usage.active_users_7d} color="text-emerald-600" />
            <MetricCard icon={BookOpen} label="Total Courses" value={data.tenant_usage.total_courses} />
            <MetricCard icon={Layers} label="Total Enrollments" value={data.tenant_usage.total_enrollments} />
          </div>
        </CardContent>
      </Card>
    )}

    {/* Performance */}
    {data.performance && (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Gauge className="w-5 h-5 text-[#1e3a5f]" />
            Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div>
              <div className="text-3xl font-bold text-[#0f1b2d]">{data.performance.db_latency_ms}ms</div>
              <div className="text-xs text-slate-500">Database Latency</div>
            </div>
            <Separator orientation="vertical" className="h-12" />
            <div>
              <Badge variant={data.performance.api_status === "healthy" ? "default" : "destructive"}>
                API {data.performance.api_status}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    )}

    <SystemInfoFooter data={data} />
  </div>
);

// ═══════════════════════════════════════
// SUPER ADMIN VIEW (Full Infrastructure)
// ═══════════════════════════════════════
const SuperAdminHealthView = ({ data, loading, onRefresh }) => {
  const server = data.server || {};
  const database = data.database || {};
  const redis = data.redis || {};
  const platformTotals = data.platform_totals || {};
  const errors24h = data.errors_24h || {};
  const auditLogs = data.audit_logs || {};
  const performance = data.performance || {};
  const rateLimits = data.rate_limits || {};
  const topCollections = database.top_collections || [];
  const maxDocs = topCollections.length > 0 ? topCollections[0].documents : 1;

  return (
    <div className="space-y-6" data-testid="super-admin-health-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0f1b2d]">System Health Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Complete infrastructure monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-400">
            Auto-refresh: 30s
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall status + uptime */}
      <OverallStatusBanner data={data} showUptime />

      {/* Services grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {Object.values(data.services || {}).map((s, i) => (
          <ServiceCard key={i} service={s} />
        ))}
      </div>

      <Tabs defaultValue="infrastructure" className="w-full">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="infrastructure" data-testid="tab-infrastructure">Infrastructure</TabsTrigger>
          <TabsTrigger value="database" data-testid="tab-database">Database</TabsTrigger>
          <TabsTrigger value="cache" data-testid="tab-cache">Cache & Limits</TabsTrigger>
          <TabsTrigger value="platform" data-testid="tab-platform">Platform</TabsTrigger>
        </TabsList>

        {/* ── Infrastructure Tab ── */}
        <TabsContent value="infrastructure" className="space-y-6 mt-4">
          {/* Server Gauges */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="w-5 h-5 text-[#1e3a5f]" />
                Server Resources
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 py-4">
                <GaugeRing percent={server.cpu_usage_percent || 0} label="CPU Usage" />
                <GaugeRing percent={server.memory_percent || 0} label="Memory Usage" />
                <GaugeRing percent={server.disk_percent || 0} label="Disk Usage" />
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="text-4xl font-bold text-[#1e3a5f]">{server.cpu_cores || "—"}</div>
                  <div className="text-xs text-slate-500 font-medium">CPU Cores</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Server Details Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={MemoryStick} label="Total Memory" value={server.memory_total || "—"} sub={`Available: ${server.memory_available || "—"}`} />
            <MetricCard icon={HardDrive} label="Disk Space" value={server.disk_total || "—"} sub={`Free: ${server.disk_free || "—"}`} />
            <MetricCard icon={Cpu} label="Python Version" value={server.python_version || "—"} />
            <MetricCard icon={Server} label="Operating System" value={(server.os || "—").split(" ")[0]} sub={server.os} />
          </div>

          {/* Performance */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Gauge className="w-5 h-5 text-[#1e3a5f]" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-3xl font-bold text-[#0f1b2d]">{performance.db_latency_ms || "—"}ms</div>
                  <div className="text-xs text-slate-500 mt-1">DB Latency</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-3xl font-bold text-[#0f1b2d]">{data.active_sessions || 0}</div>
                  <div className="text-xs text-slate-500 mt-1">Active Sessions (30m)</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-3xl font-bold text-[#0f1b2d]">{data.uptime || "—"}</div>
                  <div className="text-xs text-slate-500 mt-1">Uptime</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-3xl font-bold text-[#0f1b2d]">v{data.version || "—"}</div>
                  <div className="text-xs text-slate-500 mt-1">API Version</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Database Tab ── */}
        <TabsContent value="database" className="space-y-6 mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={Database} label="MongoDB Version" value={database.version || "—"} />
            <MetricCard icon={Layers} label="Collections" value={database.collections || 0} />
            <MetricCard icon={BarChart3} label="Total Documents" value={(database.total_documents || 0).toLocaleString()} />
            <MetricCard icon={HardDrive} label="Data Size" value={database.data_size || "—"} sub={`Index: ${database.index_size || "—"}`} />
          </div>

          {/* Connection Pool */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#1e3a5f]" />
                Connection Pool
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-sm text-slate-500 mb-1">Max Pool Size</div>
                  <div className="text-2xl font-bold text-[#0f1b2d]">{database.connection_pool?.max_pool_size || "—"}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Min Pool Size</div>
                  <div className="text-2xl font-bold text-[#0f1b2d]">{database.connection_pool?.min_pool_size || "—"}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Collections */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-5 h-5 text-[#1e3a5f]" />
                Top Collections by Document Count
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {topCollections.map((c, i) => (
                  <CollectionRow key={i} name={c.name} documents={c.documents} maxDocs={maxDocs} />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Cache & Rate Limits Tab ── */}
        <TabsContent value="cache" className="space-y-6 mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={Zap} label="Redis Version" value={redis.version || "—"} />
            <MetricCard icon={Activity} label="Hit Rate" value={`${redis.hit_rate || 0}%`} color="text-emerald-600" />
            <MetricCard icon={MemoryStick} label="Memory Used" value={redis.used_memory || "—"} sub={`Peak: ${redis.peak_memory || "—"}`} />
            <MetricCard icon={BarChart3} label="Commands Processed" value={(redis.total_commands || 0).toLocaleString()} />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#1e3a5f]" />
                Cache Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <div className="text-sm text-slate-500 mb-1">Cached Responses</div>
                  <div className="text-2xl font-bold text-[#0f1b2d]">{redis.cached_responses ?? "—"}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Cache Hits</div>
                  <div className="text-2xl font-bold text-emerald-600">{(redis.keyspace_hits || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Cache Misses</div>
                  <div className="text-2xl font-bold text-amber-600">{(redis.keyspace_misses || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Rate Limit Entries</div>
                  <div className="text-2xl font-bold text-[#0f1b2d]">{redis.rate_limit_entries ?? "—"}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rate Limits */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#1e3a5f]" />
                Rate Limiting Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(rateLimits).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="text-sm font-medium text-slate-700 capitalize">{key.replace(/_/g, " ")}</div>
                    <Badge variant="outline" className="font-mono text-xs">{value}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Platform Tab ── */}
        <TabsContent value="platform" className="space-y-6 mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard icon={Building2} label="Tenants" value={platformTotals.tenants || 0} />
            <MetricCard icon={Users} label="Users" value={platformTotals.users || 0} />
            <MetricCard icon={BookOpen} label="Courses" value={platformTotals.courses || 0} />
            <MetricCard icon={Layers} label="Enrollments" value={platformTotals.enrollments || 0} />
          </div>

          {/* Errors */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Errors (Last 24 Hours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <div>
                  <div className="text-3xl font-bold text-[#0f1b2d]">{errors24h.total || 0}</div>
                  <div className="text-xs text-slate-500">Total Errors</div>
                </div>
                {Object.entries(errors24h.by_severity || {}).map(([sev, count]) => (
                  <div key={sev}>
                    <div className={cn(
                      "text-2xl font-bold",
                      sev === "critical" ? "text-red-600" : sev === "error" ? "text-orange-500" : "text-amber-500"
                    )}>{count}</div>
                    <div className="text-xs text-slate-500 capitalize">{sev}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Audit Logs */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#1e3a5f]" />
                Audit Log Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <div className="text-sm text-slate-500 mb-1">Total Entries</div>
                  <div className="text-2xl font-bold text-[#0f1b2d]">{(auditLogs.total || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Last 24h</div>
                  <div className="text-2xl font-bold text-[#0f1b2d]">{auditLogs.last_24h || 0}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">TTL Auto-Cleanup</div>
                  <Badge variant={auditLogs.ttl_enabled ? "default" : "outline"}>
                    {auditLogs.ttl_enabled ? `Enabled (${auditLogs.ttl_days}d)` : "Disabled"}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Active Sessions</div>
                  <div className="text-2xl font-bold text-[#0f1b2d]">{data.active_sessions || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <SystemInfoFooter data={data} />
    </div>
  );
};

// ── Shared Components ──
const OverallStatusBanner = ({ data, showUptime = false }) => {
  const cfg = STATUS_CONFIG[data.overall_status] || STATUS_CONFIG.degraded;
  const Icon = cfg.icon;
  return (
    <div className={cn("flex items-center justify-between p-4 rounded-xl border", cfg.bg, cfg.border)} data-testid="overall-status-banner">
      <div className="flex items-center gap-3">
        <Icon className={cn("w-6 h-6", cfg.text)} />
        <div>
          <div className={cn("font-bold text-sm", cfg.text)}>
            {data.overall_status === "operational" ? "All Systems Operational" : data.overall_status === "degraded" ? "Partial System Degradation" : "System Outage Detected"}
          </div>
          <div className="text-xs text-slate-500">
            {Object.values(data.services || {}).filter(s => s.status === "operational").length} of{" "}
            {Object.values(data.services || {}).length} services operational
          </div>
        </div>
      </div>
      {showUptime && data.uptime && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Clock className="w-3.5 h-3.5" />
          Uptime: {data.uptime}
        </div>
      )}
    </div>
  );
};

const SystemInfoFooter = ({ data }) => (
  <div className="flex items-center justify-between py-3 px-1 text-xs text-slate-400" data-testid="system-info-footer">
    <span>Odesie LMS v{data.version || "—"}</span>
    <span>Server Time: {data.server_time ? new Date(data.server_time).toLocaleString() : "—"}</span>
  </div>
);

// ═══════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════
const SystemHealthPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const role = user?.role || "learner";

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/system/health");
      setData(res.data);
    } catch {
      // Keep stale data on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const layoutVariant =
    role === "super_admin" ? "super_admin"
    : role === "tenant_admin" ? "tenant_admin"
    : role === "manager" ? "manager"
    : "learner";

  if (!data && loading) {
    return (
      <AdminLayout variant={layoutVariant}>
        <div className="flex items-center justify-center h-[60vh]" data-testid="health-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      </AdminLayout>
    );
  }

  const viewProps = { data: data || {}, loading, onRefresh: fetchHealth };

  return (
    <AdminLayout variant={layoutVariant}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {role === "super_admin" ? <SuperAdminHealthView {...viewProps} /> :
         role === "tenant_admin" ? <TenantAdminHealthView {...viewProps} /> :
         role === "manager" ? <ManagerHealthView {...viewProps} /> :
         <LearnerHealthView {...viewProps} />}
      </div>
    </AdminLayout>
  );
};

export default SystemHealthPage;
